import { Constants } from '@truckdown/systems';

import context from '@truckdown/components-systems';
import { NoteModel, INoteManager, EditNoteModel } from '@truckdown/locations';
import { ILookupService, RegionModel } from '@truckdown/systems';
import { defineComponent, ref, Ref, computed } from 'vue';

interface IProps {
    isAdmin: boolean;
    note: NoteModel;
}

interface IData {
    showDelete: Ref<boolean>,
    showEdit: Ref<boolean>,
    comment: Ref<string>,

    showCreateTask: Ref<boolean>
}

const getData = function (props: IProps) {
    return {
        showDelete: ref<boolean>(false),
        showEdit: ref<boolean>(false),
        comment: ref<string>(''),
        showCreateTask: ref<boolean>(false)
    } as IData;
}

const getComputed = function (data: IData, props: IProps) {
    return {
        vendorUrl: computed(() => {
            if (props.isAdmin) {
                return '/accounts-admin/view-account/' + encodeURIComponent(props.note.vendorId);
            } else {
                return '/admin';
            }
        }),
        locationUrl: computed(() => {
            if (props.isAdmin) {
                return '/location-admin/view-location/' + encodeURIComponent(props.note.locationId);
            } else {
                return '/search/view-detail/' + encodeURIComponent(props.note.location.replace(/ /g, '_').toLowerCase()) + '/' + encodeURIComponent(props.note.locationId);
            }
        }),
        accountUrl: computed(() => {
            if (props.isAdmin) {
                return '/accounts-admin/view-account/' + encodeURIComponent(props.note.accountId);
            } else {
                return '/admin';
            }
        }),
        userUrl: computed(() => {
            if (props.isAdmin) {
                return '/accounts-admin/view-user/' + encodeURIComponent(props.note.userId);
            } else {
                if (context.user?.id == props.note.userId) {
                    return '/settings/user/settings';
                } else {
                    return '/settings/user/administration';
                }
            }
        }),
        canEdit: computed(() => {
            if (!context.claims.some(c => {
                return c.type == 'search_edit_notes' && c.val == 'True'
            })) {
                return false;
            }

            if (context.isInRole(Constants.Roles.FleetUserRole)) {
                return props.note.accountId == context.user?.accountId;
            } else {
                return props.note.accountId == context.user?.accountId
                    && props.note.userId == context.user?.id;
            }
        }),
        canProcess: computed(() => {
            return props.isAdmin 
                && (context.isInRole(Constants.Roles.SystemRole))
                && !props.note.processed;
        }),
        canUnprocess: computed(() => {
            return props.isAdmin
                && (context.isInRole(Constants.Roles.SystemRole))
                && props.note.processed;
        })
    };
}

const getMethods = function (ctx: any, data: IData, props: IProps) {
    return {
        formatDate: function (dt: string | Date) {
            return new Date(dt).toLocaleString();
        },
        process: function (ev: Event) {
            context.getService<INoteManager>('INoteManager')
                .then(async (service) => {
                    let res = await service.process(props.note.id);
                    if (res.ok) {
                        props.note.processed = true;
                        ctx.emit('onEdit', props.note);
                    } else {
                        context.events.emit('show-message', {
                            message: 'There was an error processing the note.',
                            messageType: 'danger',
                            dismissable: true,
                            autoDismiss: true
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                    context.events.emit('show-message', {
                        message: 'There was an error processing the note.',
                        messageType: 'danger',
                        dismissable: true,
                        autoDismiss: true
                    });
                });
        },
        unprocess: function (ev: Event) {
            context.getService<INoteManager>('INoteManager')
                .then(async (service) => {
                    let res = await service.unprocess(props.note.id);
                    if (res.ok) {
                        props.note.processed = false;
                        ctx.emit('onEdit', props.note);
                    } else {
                        context.events.emit('show-message', {
                            message: 'There was an error unprocessing the note.',
                            messageType: 'danger',
                            dismissable: true,
                            autoDismiss: true
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                    context.events.emit('show-message', {
                        message: 'There was an error unprocessing the note.',
                        messageType: 'danger',
                        dismissable: true,
                        autoDismiss: true
                    });
                });
        },
        openEdit: function (ev: Event) {
            data.comment.value = props.note.comment.trim();
            data.showEdit.value = true;
        },
        closeEdit: function (ev: Event) {
            data.comment.value = '';
            data.showEdit.value = false;
        },
        editNote: function (ev: Event) {
            if (data.comment.value.trim().length == 0) {
                this.deleteNote(ev);
            } else {
                if (props.note.comment.trim() == data.comment.value.trim()) {
                    data.showEdit.value = false;
                } else {
                    context.getService<INoteManager>('INoteManager')
                        .then(async (service) => {
                            let res = await service.editNote({
                                id: props.note.id,
                                comment: data.comment.value
                            } as EditNoteModel);
                            if (res.ok) {
                                ctx.emit('onEdit', res.data);
                            } else {
                                context.events.emit('show-message', {
                                    message: 'There was an error editing the note.',
                                    messageType: 'danger',
                                    dismissable: true,
                                    autoDismiss: true
                                });
                            }
                        })
                        .catch((err) => {
                            console.log(err);
                            context.events.emit('show-message', {
                                message: 'There was an error editing the note.',
                                messageType: 'danger',
                                dismissable: true,
                                autoDismiss: true
                            });
                        })
                        .finally(() => {
                            data.showEdit.value = false;
                        });
                }
            }
        },
        openDelete: function (ev: Event) {
            data.showDelete.value = true;
        },
        closeDelete: function (ev: Event) {
            data.showDelete.value = false;
        },
        deleteNote: function (ev: Event) {
            context.getService<INoteManager>('INoteManager')
                .then(async (service) => {
                    let res = await service.deleteNote(props.note.id);
                    if (res.ok) {
                        ctx.emit('onDelete', props.note.id);
                    } else {
                        context.events.emit('show-message', {
                            message: 'There was an error deleting the note.',
                            messageType: 'danger',
                            dismissable: true,
                            autoDismiss: true
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                    context.events.emit('show-message', {
                        message: 'There was an error deleting the note.',
                        messageType: 'danger',
                        dismissable: true,
                        autoDismiss: true
                    });
                })
                .finally(() => {
                    data.showDelete.value = false;
                });
        },
        createTask: function (ev: Event) {
            data.showCreateTask.value = true;
        },
        closeCreateTask: function () {
            data.showCreateTask.value = false;
        }
    };
}

export default defineComponent({
    props: {
        isAdmin: Boolean,
        note: {
            type: Object as () => NoteModel,
            required: true
        }
    },
    emits: [
        'onEdit', 'onDelete'
    ],
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(data, props);
        let methods = getMethods(ctx, data, props);

        return {
            ...data,
            ...computed,
            ...methods,
            isFleet: context.isInRole(Constants.Roles.FleetUserRole)
        };
    }
});